import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore';
import { sfx } from '../services/sfx';

// A node IP that can be tapped to load it straight into the game screen's Target IP
// field — typing IPs by hand is painful on a phone. Used by the Targets and Hacked-Nodes
// lists and by IPs found inside file contents (e.g. the looted servers.txt).
//
// While a remote session is open the IP is rendered as plain text: the target field can
// only be set from a disconnected state, so offering a click there would be a dead end.
//
// Styling stays inline with zero padding/border so it never breaks the monospace
// alignment of the tables and file listings it appears in.

interface Props {
  ip: string;
  /** Colour of the IP text (defaults to the "live node" green). */
  color?: string;
  /**
   * Handle the pick locally instead of navigating — used by the file-reader modal, which
   * is already on the game screen and just needs to fill the field and close itself.
   */
  onPick?: (ip: string) => void;
}

export default function TargetIpLink({ ip, color = '#4ade80', onPick }: Props) {
  const navigate = useNavigate();
  const connected = useSessionStore(s => s.session) != null;

  if (connected) {
    return (
      <span style={{ color }} title="Disconnect to target another node">{ip}</span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        sfx.click();
        if (onPick) onPick(ip);
        else navigate(`/game?target=${encodeURIComponent(ip)}`);
      }}
      title="Use as target"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color,
        textDecoration: 'underline dotted',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
      }}
    >
      {ip}
    </button>
  );
}
